import { useEffect, useState } from "react";
import { confirmAction } from "../utils/confirm";
import AppModal from "./AppModal";
import Pagination from "./Pagination/Pagination";
import FilterBar, { FilterField } from "./FilterBar";
import { useListController } from "../controllers/useListController";
import { usePermission } from "../hooks/usePermission";
import { showToast } from "../utils/toast";
import { validateForm } from "../utils/validate";
import { ACTIVE_STATUS_FILTERS, emptyMessageForStatus, statusFromFilter } from "../utils/listFilters";
import { API_CONFIG } from "../config/api";

const EMPTY = {
  id: null,
  title: "",
  description: "",
  is_active: 1,
  file: null,
};

/**
 * Reusable PDF-document manager for both Documents and Policies. They share the
 * same shape (title, description, PDF), so this single component drives both
 * screens with `service`, `moduleCode` and `noun` props. All items are
 * universal/group-wide — there is no scope.
 */
export default function FileDocsManager({ service, moduleCode, noun, nounPlural }) {
  const plural = nounPlural || `${noun}s`;
  const { rows, pagination, loading, error, fetchRows } =
    useListController(service);
  const { can } = usePermission();
  const canAdd = can(moduleCode, "is_add");
  const canEdit = can(moduleCode, "is_update");
  const canDelete = can(moduleCode, "is_delete");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [search, setSearch] = useState("");
  const statusFilters = ACTIVE_STATUS_FILTERS(`All ${plural.toLowerCase()}`);

  const queryParams = (extra = {}) => ({
    page: extra.page ?? pagination.page,
    limit: extra.limit ?? pagination.limit,
    status: statusFromFilter(extra.filter ?? viewFilter, statusFilters),
    search: extra.search ?? search,
  });

  useEffect(() => {
    fetchRows({ page: 1, status: statusFromFilter(viewFilter, statusFilters), search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchRows({ page: 1, status: statusFromFilter(viewFilter, statusFilters), search });
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setSearch("");
    fetchRows({ page: 1, status: "", search: "" });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = emptyMessageForStatus(viewFilter, {
    all: `No ${plural.toLowerCase()} yet`,
    active: `No active ${plural.toLowerCase()}`,
    inactive: `No inactive ${plural.toLowerCase()}`,
  });

  const openAdd = () => {
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title,
      description: row.description || "",
      is_active: row.is_active,
      file: null,
    });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      setForm((f) => ({ ...f, file: files[0] || null }));
    } else {
      setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !validateForm(e.currentTarget, [
        { name: "title", value: form.title, label: "Title", required: true },
        { name: "description", value: form.description, label: "Description", required: true },
        {
          name: "file",
          value: form.file,
          label: "PDF file",
          custom: () =>
            !form.id && !form.file ? "A PDF file is required" : null,
        },
      ])
    )
      return;
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        is_active: form.is_active ? 1 : 0,
      };
      if (form.id) payload.id = form.id;
      // Map the file to the multer field name (document / policy).
      if (form.file) payload[moduleCode === "policies" ? "policy" : "document"] = form.file;

      const res = form.id ? await service.update(payload) : await service.create(payload);
      if (res?.success) {
        showToast("success", form.id ? `${noun} updated` : `${noun} added`);
        setModalOpen(false);
        fetchRows(queryParams());
      } else {
        showToast("error", res?.msg || "Save failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (row) => {
    const res = await service.updateStatus(row.id, row.is_active ? 0 : 1);
    if (res?.success) {
      showToast("success", "Status updated");
      fetchRows(queryParams());
    }
  };

  const handleDelete = async (row) => {
    const r = await confirmAction({
      title: `Delete "${row.title}"?`,
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!r.isConfirmed) return;
    const res = await service.delete(row.id);
    if (res?.success) {
      showToast("success", `${noun} deleted`);
      fetchRows(queryParams());
    } else showToast("error", res?.msg || "Failed");
  };

  const fileUrl = (path) =>
    path ? API_CONFIG.ASSET_BASE_URL + "uploads/" + path.replace(/^uploads\//, "") : null;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>{plural}</strong>
        {canAdd && (
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="cil-plus me-1" /> Add {noun}
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id={`${moduleCode}-status-filter`} label="Show">
            <select
              id={`${moduleCode}-status-filter`}
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {statusFilters.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id={`${moduleCode}-search`} label="Search">
            <input
              id={`${moduleCode}-search`}
              className="form-control"
              placeholder="Title or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>File</th>
                <th>Status</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="fw-semibold">{r.title}</td>
                    <td className="small text-muted">{r.description || "—"}</td>
                    <td>
                      {r.file_path ? (
                        <a href={fileUrl(r.file_path)} target="_blank" rel="noreferrer">
                          <i className="cil-cloud-download me-1" />
                          {r.file_label || "PDF"}
                        </a>
                      ) : (
                        <span className="text-muted">No file</span>
                      )}
                    </td>
                    <td>
                      {r.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>Edit</button>
                        )}
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggle(r)}>
                            {r.is_active ? "Hide" : "Show"}
                          </button>
                        )}
                        {canDelete && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => fetchRows({ ...queryParams(), page })}
          onLimitChange={(limit) => fetchRows({ ...queryParams(), page: 1, limit })}
        />
      </div>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? `Edit ${noun}` : `Add ${noun}`}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" form="filedoc-form" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : form.id ? "Save Changes" : "Create"}
            </button>
          </>
        }
      >
              <form id="filedoc-form" onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <label className="form-label">Title <span className="text-danger">*</span></label>
                  <input name="title" className="form-control" value={form.title} onChange={onChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description <span className="text-danger">*</span></label>
                  <textarea name="description" className="form-control" rows={2} value={form.description} onChange={onChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    PDF file <span className="text-danger">*</span>
                    {form.id && <span className="text-muted small"> (leave blank to keep current)</span>}
                  </label>
                  <input type="file" name="file" accept="application/pdf" className="form-control" onChange={onChange} />
                </div>
                <div className="form-check">
                  <input type="checkbox" id="fdActive" className="form-check-input" name="is_active" checked={!!form.is_active} onChange={onChange} />
                  <label htmlFor="fdActive" className="form-check-label ms-1">Active (visible to families)</label>
                </div>
              </form>
      </AppModal>
    </div>
  );
}
